/**
 * common/lib/shape.ts -> chart-tools/src/types/shape.ts 로 자동 복사됨.
 * Shape Properties Zod Types
 *
 * 차트 도형의 속성을 정의하는 Zod 스키마들입니다.
 * chart-tools와 engine 모두에서 사용됩니다.
 */
import { z } from 'zod/v4';

// ============================================================================
// Stroke Dash Types
// ============================================================================

/** 선 스타일 타입 (react-financial-charts의 strokeDashTypes와 동일) */
export const strokeDashTypesZodType = z.union([
  z.literal('Solid'),
  z.literal('ShortDash'),
  z.literal('ShortDash2'),
  z.literal('ShortDot'),
  z.literal('ShortDashDot'),
  z.literal('ShortDashDotDot'),
  z.literal('Dot'),
  z.literal('Dash'),
  z.literal('LongDash'),
  z.literal('DashDot'),
  z.literal('LongDashDot'),
  z.literal('LongDashDotDot'),
]);
export type StrokeDashTypes = z.infer<typeof strokeDashTypesZodType>;

// ============================================================================
// Y Accessor Value
// ============================================================================

export const yAccessorValueZodType = z.union([z.string(), z.number()]);
export type YAccessorValue = z.infer<typeof yAccessorValueZodType>;

// ============================================================================
// Coordinate
// ============================================================================

/** x, y coordinates. x is the unix-timestamp value, y is the price */
export const CoordinateZodType = z.tuple([z.number(), z.number()]);
export type Coordinate = z.infer<typeof CoordinateZodType>;

// ============================================================================
// Line Options
// ============================================================================

/** Line width & Dash style */
export const LineWidthDashOptionZodType = z.object({
  strokeWidth: z.coerce.number(),
  strokeDasharray: strokeDashTypesZodType,
});
export type LineWidthDashOption = z.infer<typeof LineWidthDashOptionZodType>;

/** Line Style & Width & Dash style */
export const LineOptionZodType = LineWidthDashOptionZodType.extend({
  /** #rgba hex format */
  strokeStyle: z.string(),
});
export type LineOption = z.infer<typeof LineOptionZodType>;

/** Line Style & Width & Dash style & visible */
export const ShowLineOptionZodType = LineOptionZodType.extend({
  show: z.boolean(),
});
export type ShowLineOption = z.infer<typeof ShowLineOptionZodType>;

export const AltLineOptionZodType = ShowLineOptionZodType.extend({
  strokeStyle2: z.string(),
});
export type AltLineOption = z.infer<typeof AltLineOptionZodType>;

// ============================================================================
// Color Options
// ============================================================================

/** background visibility & background style */
export const ShowColorOptionZodType = z.object({
  show: z.boolean(),
  /** #rgba hex format */
  color: z.string(),
});
export type ShowColorOption = z.infer<typeof ShowColorOptionZodType>;

/** Color only (no show property, for CANDLE series) */
export const ColorOptionZodType = z.object({
  /** #rgba hex format */
  color: z.string(),
});
export type ColorOption = z.infer<typeof ColorOptionZodType>;

/** Fibonacci or Gann shape's level ratio and color */
export const ShowValueColorOptionZodType = ShowColorOptionZodType.extend({
  /** level ratio */
  value: z.number(),
});
export type ShowValueColorOption = z.infer<typeof ShowValueColorOptionZodType>;

/** Fibonacci or Gann shape's level background option */
export const ShowOpacityOptionZodType = z.object({
  show: z.boolean(),
  /** float between 0 ~ 1 */
  opacity: z.number(),
});
export type ShowOpacityOption = z.infer<typeof ShowOpacityOptionZodType>;

// ============================================================================
// Font Options
// ============================================================================

/** Font style */
export const FontOptionZodType = z.object({
  /** #rgba hex format */
  fill: z.string(),
  fontFamily: z.string(),
  /** pixel unit */
  fontSize: z.number(),
  fontWeight: z.union([z.string(), z.number()]),
  /** supports only 'italic' */
  fontStyle: z.string(),
});
export type FontOption = z.infer<typeof FontOptionZodType>;

/** Font Basic style (same as "FontOption" except there is no font-family */
export const FontBasicOptionZodType = FontOptionZodType.omit({
  fontFamily: true,
});
export type FontBasicOption = z.infer<typeof FontBasicOptionZodType>;

// ============================================================================
// Vertex Type
// ============================================================================

/** 화살표/일반 꼭짓점 타입 */
export enum VertexType {
  NORMAL = 'normal',
  ARROW = 'arrow',
}

// ============================================================================
// Shape 관련 타입 정의
// ============================================================================

/**
 * Chart ID 타입 - 'chartId_' 접두사로 시작하는 문자열
 * 예: 'chartId_BTC/USDT:USDT_5m_candle'
 */
export type ChartId = `chartId_${string}`;

/**
 * Shape 타입 열거형
 * chart-tools의 ShapeType과 동일한 값
 */
export enum ShapeType {
  MEASURE = 'Measure',
  TREND_LINE = 'TrendLine',
  RAY = 'Ray',
  INFO_LINE = 'InfoLine',
  HORZ_LINE = 'HorzLine',
  VERT_LINE = 'VertLine',
  PARALLEL_CHANNEL = 'ParallelChannel',
  FIB_RETRACEMENT = 'FibRetracement',
  FIB_CHANNEL = 'FibChannel',
  GANN_BOX = 'GannBox',
  GANN_FAN = 'GannFan',
  ELLIOTT_IMPULSE_12345 = 'ElliottImpulse12345',
  ELLIOTT_CORRECTION_ABC = 'ElliottCorrectionABC',
  XABCD_PATTERN = 'XABCDPattern',
  RECTANGLE = 'Rectangle',
  TRIANGLE = 'Triangle',
  PATH = 'Path',
  TEXT = 'Text',
  FIXED_RANGE_VOLUME_PROFILE = 'FixedRangeVolumeProfile',
  ANCHORED_VOLUME_PROFILE = 'AnchoredVolumeProfile',
  ORDER_LINE = 'OrderLine',
}

// 조건에서 참조 가능한 도형 종류
export const CONDITIONABLE_SHAPE_TYPES = [
  ShapeType.TREND_LINE,
  ShapeType.RAY,
  ShapeType.INFO_LINE,
  ShapeType.HORZ_LINE,
  ShapeType.PARALLEL_CHANNEL,
  ShapeType.FIB_RETRACEMENT,
  ShapeType.FIB_CHANNEL,
  ShapeType.GANN_BOX,
  ShapeType.GANN_FAN,
  ShapeType.RECTANGLE,
  ShapeType.TRIANGLE,
] as const;

export type ConditionableShapeType = (typeof CONDITIONABLE_SHAPE_TYPES)[number];

export function isConditionable(shapeType: ShapeType): shapeType is ConditionableShapeType {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return CONDITIONABLE_SHAPE_TYPES.includes(shapeType as ConditionableShapeType);
}

/**
 * 레벨 정보 (피보나치, 간 등에서 사용)
 */
export interface ShapeLevel {
  show: boolean;
  value: number;
  color: string;
}

/**
 * 좌표 타입 - [timestamp, price]
 * @deprecated Coordinate를 사용하세요
 */
export type ShapeCoordinate = Coordinate;

// ============================================================================
// 도형별 Zod 스키마
// ============================================================================

const VertexTypeZodType = z.enum(VertexType);

const ArrowZodType = z.tuple([VertexTypeZodType, VertexTypeZodType]);

const ExtendZodType = z.tuple([z.boolean(), z.boolean()]);

/** BaseStorageShape 공통 필드 */
const BaseStorageShapeZodType = z.object({
  uuid: z.string(),
  /** 사용자 표시용 ID (조건 편집기 등에서 참조). 서버에서 자동 발급. */
  id: z.number().optional(),
  type: z.enum(ShapeType),
  chartId: z.templateLiteral(['chartId_', z.string()]),
  coordinates: z.array(CoordinateZodType),
  order: z.number().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  /** 사용자가 도형 목록에서 숨김 처리한 상태. F5 갱신 후에도 유지되어야 하므로 영구 저장. */
  hidden: z.boolean().optional(),
  /** 사용자가 도형을 잠금 처리한 상태. F5 갱신 후에도 유지되어야 하므로 영구 저장. */
  locked: z.boolean().optional(),
});

/** ExtensibleInfoLine 공통 필드 (TrendLine/Ray/InfoLine) */
const ExtensibleInfoLineFieldsZodType = z.object({
  line: LineOptionZodType,
  arrow: ArrowZodType,
  extend: ExtendZodType,
  showPriceRange: z.boolean(),
  showPercentChange: z.boolean(),
  showBarsRange: z.boolean(),
  showDateTimeRange: z.boolean(),
  showAngle: z.boolean(),
  alwaysShow: z.boolean(),
});

export const TrendLineStorageShapeZodType = BaseStorageShapeZodType.extend(
  ExtensibleInfoLineFieldsZodType.shape,
).extend({
  type: z.literal(ShapeType.TREND_LINE),
});

export const RayStorageShapeZodType = BaseStorageShapeZodType.extend(ExtensibleInfoLineFieldsZodType.shape).extend({
  type: z.literal(ShapeType.RAY),
});

export const InfoLineStorageShapeZodType = BaseStorageShapeZodType.extend(ExtensibleInfoLineFieldsZodType.shape).extend(
  {
    type: z.literal(ShapeType.INFO_LINE),
  },
);

export const HorzLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.HORZ_LINE),
  line: LineOptionZodType,
});

export const VertLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.VERT_LINE),
  line: LineOptionZodType,
  /** VertLine이 그려진 원본 chartId (chartId_common과 별도) */
  originChartId: z.templateLiteral(['chartId_', z.string()]).optional(),
});

export const ElliottImpulseStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.ELLIOTT_IMPULSE_12345),
  line: LineOptionZodType,
});

export const ElliottCorrectionABCStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.ELLIOTT_CORRECTION_ABC),
  line: LineOptionZodType,
});

export const FibRetracementStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.FIB_RETRACEMENT),
  trendLine: LineOptionZodType,
  levelsLine: LineWidthDashOptionZodType,
  extend: ExtendZodType,
  levels: z.array(ShowValueColorOptionZodType),
  background: ShowOpacityOptionZodType,
});

export const FibChannelStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.FIB_CHANNEL),
  levelsLine: LineWidthDashOptionZodType,
  extend: ExtendZodType,
  levels: z.array(ShowValueColorOptionZodType),
  background: ShowOpacityOptionZodType,
});

export const GannBoxStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.GANN_BOX),
  priceLevels: z.array(ShowValueColorOptionZodType),
  priceBackground: ShowOpacityOptionZodType,
  timeLevels: z.array(ShowValueColorOptionZodType),
  timeBackground: ShowOpacityOptionZodType,
});

/** GannFan 레벨 라벨 (levels 배열 인덱스 순서와 1:1 대응) */
export const GANN_FAN_LABELS = ['1/8', '1/4', '1/3', '1/2', '1/1', '2/1', '3/1', '4/1', '8/1'] as const;

export const GannFanStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.GANN_FAN),
  levels: z.array(ShowColorOptionZodType),
  background: ShowOpacityOptionZodType,
});

export const TextStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.TEXT),
  text: z.string(),
  font: FontOptionZodType,
  background: ShowColorOptionZodType,
});

export const ParallelChannelStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.PARALLEL_CHANNEL),
  channel: LineOptionZodType,
  middle: ShowLineOptionZodType,
  extend: ExtendZodType,
  background: ShowColorOptionZodType,
});

export const RectangleStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.RECTANGLE),
  coordinates: z.array(CoordinateZodType),
  border: LineOptionZodType,
  middle: ShowLineOptionZodType,
  background: ShowColorOptionZodType,
  extend: ExtendZodType,
});

export const TriangleStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.TRIANGLE),
  coordinates: z.array(CoordinateZodType),
  border: LineOptionZodType,
  background: ShowColorOptionZodType,
});

export const PathStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.PATH),
  coordinates: z.array(CoordinateZodType),
  line: LineOptionZodType,
  arrow: ArrowZodType,
});

export const XABCDPatternStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.XABCD_PATTERN),
  coordinates: z.array(CoordinateZodType),
  label: FontOptionZodType,
  border: LineOptionZodType,
  background: ShowColorOptionZodType,
});

/** Volume Profile 공통 설정 Zod */
const VolumeProfileSettingsZodType = z.object({
  rowsLayout: z.union([z.literal('Number of Rows'), z.literal('Ticks per Row')]),
  rowSize: z.number(),
  volume: z.union([z.literal('Up/Down'), z.literal('Total'), z.literal('Delta')]),
  valueAreaVolume: z.number(),
  volumeProfile: z.boolean(),
  values: ShowColorOptionZodType,
  width: z.number(),
  placement: z.union([z.literal('left'), z.literal('right')]),
  upVolume: z.string(),
  downVolume: z.string(),
  valueAreaUp: z.string(),
  valueAreaDown: z.string(),
  vah: ShowLineOptionZodType,
  val: ShowLineOptionZodType,
  poc: ShowLineOptionZodType,
  developingPOC: ShowLineOptionZodType,
  developingVA: ShowLineOptionZodType,
  histogramBox: z.string(),
});

export const FixedRangeVolumeProfileStorageShapeZodType = BaseStorageShapeZodType.extend(
  VolumeProfileSettingsZodType.shape,
).extend({
  type: z.literal(ShapeType.FIXED_RANGE_VOLUME_PROFILE),
});

export const AnchoredVolumeProfileStorageShapeZodType = BaseStorageShapeZodType.extend(
  VolumeProfileSettingsZodType.shape,
).extend({
  type: z.literal(ShapeType.ANCHORED_VOLUME_PROFILE),
});

export const OrderLineStorageShapeZodType = BaseStorageShapeZodType.extend({
  type: z.literal(ShapeType.ORDER_LINE),
  positionType: z.union([z.literal('long'), z.literal('short')]),
  price: z.number(),
  amount: z.number(),
  tpsl: z.array(z.object({ tp: z.number(), sl: z.number() })),
});

/** 모든 저장 대상 Shape의 Zod discriminated union */
export const StorageShapeInputZodType = z.discriminatedUnion('type', [
  TrendLineStorageShapeZodType,
  RayStorageShapeZodType,
  InfoLineStorageShapeZodType,
  HorzLineStorageShapeZodType,
  VertLineStorageShapeZodType,
  ElliottImpulseStorageShapeZodType,
  ElliottCorrectionABCStorageShapeZodType,
  FibRetracementStorageShapeZodType,
  FibChannelStorageShapeZodType,
  GannBoxStorageShapeZodType,
  GannFanStorageShapeZodType,
  TextStorageShapeZodType,
  ParallelChannelStorageShapeZodType,
  RectangleStorageShapeZodType,
  TriangleStorageShapeZodType,
  PathStorageShapeZodType,
  XABCDPatternStorageShapeZodType,
  FixedRangeVolumeProfileStorageShapeZodType,
  AnchoredVolumeProfileStorageShapeZodType,
  OrderLineStorageShapeZodType,
]);

/** ShapesData의 Zod 스키마 */
export const StorageShapesMapZodType = z.record(
  z.templateLiteral(['chartId_', z.string()]),
  z.partialRecord(z.enum(ShapeType), z.array(StorageShapeInputZodType)),
);

// ============================================================================
// Shape 타입 — Zod 스키마에서 파생 (단일 소스)
// ============================================================================

/** 모든 StorageShape의 공통 속성. 판별자 필드: `type` (ShapeType) */
export type BaseStorageShape = z.infer<typeof BaseStorageShapeZodType>;

// --- Lines ---
export type TrendLineStorageShape = z.infer<typeof TrendLineStorageShapeZodType>;
export type RayStorageShape = z.infer<typeof RayStorageShapeZodType>;
export type InfoLineStorageShape = z.infer<typeof InfoLineStorageShapeZodType>;
export type HorzLineStorageShape = z.infer<typeof HorzLineStorageShapeZodType>;
export type VertLineStorageShape = z.infer<typeof VertLineStorageShapeZodType>;

// --- Elliott Wave ---
export type ElliottImpulseStorageShape = z.infer<typeof ElliottImpulseStorageShapeZodType>;
export type ElliottCorrectionABCStorageShape = z.infer<typeof ElliottCorrectionABCStorageShapeZodType>;

// --- Fibonacci ---
export type FibRetracementStorageShape = z.infer<typeof FibRetracementStorageShapeZodType>;
export type FibChannelStorageShape = z.infer<typeof FibChannelStorageShapeZodType>;

// --- Gann ---
export type GannBoxStorageShape = z.infer<typeof GannBoxStorageShapeZodType>;
export type GannFanStorageShape = z.infer<typeof GannFanStorageShapeZodType>;

// --- Text ---
export type TextStorageShape = z.infer<typeof TextStorageShapeZodType>;

// --- Channels ---
export type ParallelChannelStorageShape = z.infer<typeof ParallelChannelStorageShapeZodType>;

// --- Geometric Shapes ---
export type RectangleStorageShape = z.infer<typeof RectangleStorageShapeZodType>;
export type TriangleStorageShape = z.infer<typeof TriangleStorageShapeZodType>;
export type PathStorageShape = z.infer<typeof PathStorageShapeZodType>;

// --- Patterns ---
export type XABCDPatternStorageShape = z.infer<typeof XABCDPatternStorageShapeZodType>;

// --- Volume Profile ---
export type FixedRangeVolumeProfileStorageShape = z.infer<typeof FixedRangeVolumeProfileStorageShapeZodType>;
export type AnchoredVolumeProfileStorageShape = z.infer<typeof AnchoredVolumeProfileStorageShapeZodType>;

// --- Order Line ---
export type OrderLineStorageShape = z.infer<typeof OrderLineStorageShapeZodType>;

/**
 * Discriminated Union - 모든 저장 대상 StorageShape 타입 (20개).
 * MEASURE은 저장 비대상이므로 제외.
 * 판별자: `type` (ShapeType)
 */
export type StorageShapeInput = z.infer<typeof StorageShapeInputZodType>;
export type StorageShape = StorageShapeInput & {
  id: number;
};

// ============================================================================
// Shape 데이터 구조
// ============================================================================

/**
 * Shape 데이터 구조
 * 심볼별, Timeframe별로 저장되는 도형 데이터 (파일명: {symbol}_{timeframe}.json5)
 * 구조: chartId → shapeType → 도형 배열
 *
 * 예시:
 * {
 *   "chartId_ohlcv": {
 *     "TrendLine": [
 *       { uuid: "...", type: "TrendLine", ... },
 *       { uuid: "...", ... }
 *     ],
 *     "Rectangle": [...]
 *   },
 *   "chartId_rsi": { ... }
 * }
 */

/**
 * 엔진에 저장된 도형 맵 — 모든 도형이 id 발급 완료된 상태.
 * - 엔진 디스크 파일(JSON5), tRPC `loadShapes` 응답 등에서 사용
 */
export type StorageShapesMap = Record<ChartId, { [K in ShapeType]?: StorageShape[] }>;

/**
 * 클라이언트 측 임시 도형 맵 — id 미발급 도형 포함 가능.
 * - chart-tools 단독 모드의 localStorage
 * - tRPC `upsertShapes` 입력
 * - 그리기 직후 ~ 첫 upsert 응답 전 시점
 */
export type StorageShapesInputMap = Record<ChartId, { [K in ShapeType]?: StorageShapeInput[] }>;
